import React, { useEffect, useState } from 'react';
import {
  Title,
  Group,
  Stack,
  Card,
  Button,
  Switch,
  Select,
  TextInput,
  FileInput,
  ColorInput,
  TagsInput,
  Text,
  Divider,
  NumberInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PageContainer } from '../../components/ui/PageContainer';
import { IconBuilding } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useTenantSettings, useUpdateTenantSettings } from '../../hooks/useApi';
import { apiFormData, getErrorMessage } from '../../utils/api';
import { useAnalytics, AnalyticsEvents } from '../../analytics';
import type { ProcessDeadlines } from '../../types';
import { useCompanyModules } from '../../hooks/useCompanyModules';
import { ModuleAccessSection } from '../../components/settings/ModuleAccessSection';

/** Те же значения, что на сервере: тенант без блока сроков живёт по ним. */
const DEFAULT_DEADLINES: ProcessDeadlines = {
  essayDueDay: 5,
  quarterlyReportDueDay: 23,
  scoringOpensDay: 23,
  scoringClosesDay: 28,
  scoringClosesNextMonth: false,
};

const TIMEZONES = [
  { value: 'Europe/Moscow', label: 'Europe/Moscow (UTC+3)' },
  { value: 'Europe/London', label: 'Europe/London (UTC+0)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (UTC+1)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (UTC+1)' },
  { value: 'America/New_York', label: 'America/New_York (UTC-5)' },
  { value: 'America/Chicago', label: 'America/Chicago (UTC-6)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC-8)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (UTC+8)' },
  { value: 'UTC', label: 'UTC' },
];

const OrganizationSettings: React.FC = () => {
  const { t } = useTranslation();
  const { data: settings, isLoading: isLoadingSettings } = useTenantSettings();
  const updateSettings = useUpdateTenantSettings();
  const { track } = useAnalytics();
  // Разделы меняет платформа: здесь только список и сроки подключённых.
  // Сроки — настройка компании: по подключённым разделам, а не по своему доступу к ним.
  const { modules, hasCompanyModule } = useCompanyModules();
  const hasEssays = hasCompanyModule('essays');
  const hasQuarterly = hasCompanyModule('quarterly');
  const dayFields: Exclude<keyof ProcessDeadlines, 'scoringClosesNextMonth'>[] = [
    ...(hasEssays ? (['essayDueDay'] as const) : []),
    ...(hasQuarterly
      ? (['quarterlyReportDueDay', 'scoringOpensDay', 'scoringClosesDay'] as const)
      : []),
  ];

  const [companyName, setCompanyName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [primaryColor, setPrimaryColor] = useState('#228be6');
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [allowPublicDomains, setAllowPublicDomains] = useState(false);
  const [locale, setLocale] = useState<string | null>('ru');
  const [timezone, setTimezone] = useState<string | null>('Europe/Moscow');
  const [deadlines, setDeadlines] = useState<ProcessDeadlines>(DEFAULT_DEADLINES);
  const [isSaving, setIsSaving] = useState(false);

  // Окно оценки не может открыться позже, чем закрывается: сервер это отклонит.
  // Окно, заходящее в следующий месяц, закрывается меньшим днём — это норма.
  const deadlinesError =
    !deadlines.scoringClosesNextMonth && deadlines.scoringOpensDay > deadlines.scoringClosesDay
      ? t('settings.deadlines.orderError')
      : null;

  useEffect(() => {
    if (settings) {
      setCompanyName(settings.companyName ?? '');
      setPrimaryColor(settings.primaryColor ?? '#228be6');
      setAllowedDomains(settings.allowedDomains ?? []);
      setAllowPublicDomains(settings.allowPublicDomains ?? false);
      setLocale(settings.locale ?? 'ru');
      setTimezone(settings.timezone ?? 'Europe/Moscow');
      setDeadlines({ ...DEFAULT_DEADLINES, ...settings.deadlines });
    }
  }, [settings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        await apiFormData('/tenant/settings/logo', { method: 'POST', body: formData });
        setLogoFile(null);
      }

      await updateSettings.mutateAsync({
        companyName,
        primaryColor,
        allowedDomains,
        allowPublicDomains,
        locale: locale ?? 'ru',
        timezone: timezone ?? 'UTC',
        deadlines,
      });

      track(AnalyticsEvents.ORGANIZATION_SETTINGS_CHANGED, { field: 'organization' });
      notifications.show({
        message: t('settings.organization.saved'),
        color: 'teal',
      });
    } catch (error) {
      notifications.show({
        message: getErrorMessage(error, t('settings.organization.saveError')),
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer>
      <Group gap="md" align="center" mb={{ base: 'md', sm: 'xl' }}>
        <IconBuilding size={28} />
        <Title order={2}>{t('settings.organization.title')}</Title>
      </Group>

      <Stack gap="lg" style={{ maxWidth: '700px' }}>
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Stack gap="md">
            <TextInput
              label={t('settings.organization.companyName')}
              value={companyName}
              onChange={(e) => setCompanyName(e.currentTarget.value)}
              disabled={isLoadingSettings}
            />

            <FileInput
              label={t('settings.organization.logo')}
              accept="image/*"
              value={logoFile}
              onChange={setLogoFile}
              clearable
              disabled={isLoadingSettings}
            />

            <ColorInput
              label={t('settings.organization.primaryColor')}
              format="hex"
              value={primaryColor}
              onChange={setPrimaryColor}
              disabled={isLoadingSettings}
            />

            <TextInput
              label={t('settings.organization.signupDomains')}
              description={t('settings.organization.signupDomainsHint')}
              value={
                (settings?.signupDomains ?? []).join(', ') ||
                t('settings.organization.signupDomainsNone')
              }
              readOnly
              disabled={isLoadingSettings}
            />

            <TextInput
              label={t('settings.organization.modules')}
              description={t('settings.organization.modulesHint')}
              value={
                modules.map((key) => t(`companyModules.${key}`)).join(', ') ||
                t('settings.organization.modulesAll')
              }
              readOnly
              disabled={isLoadingSettings}
            />

            <TagsInput
              label={t('settings.organization.allowedDomains')}
              description={t('settings.organization.allowedDomainsHint')}
              value={allowedDomains}
              onChange={setAllowedDomains}
              disabled={isLoadingSettings}
              placeholder="example.com"
            />

            <Group justify="space-between">
              <Text fw={500}>{t('settings.organization.allowPublicDomains')}</Text>
              <Switch
                checked={allowPublicDomains}
                onChange={(e) => setAllowPublicDomains(e.currentTarget.checked)}
                disabled={isLoadingSettings}
              />
            </Group>

            <Select
              label={t('settings.organization.locale')}
              value={locale}
              onChange={setLocale}
              data={[
                { value: 'ru', label: 'Русский' },
                { value: 'en', label: 'English' },
              ]}
              disabled={isLoadingSettings}
            />

            <Select
              label={t('settings.organization.timezone')}
              value={timezone}
              onChange={setTimezone}
              data={TIMEZONES}
              disabled={isLoadingSettings}
              searchable
            />

            {(hasEssays || hasQuarterly) && (
              <>
                <Divider label={t('settings.deadlines.title')} labelPosition="left" mt="sm" />
                <Text size="xs" c="dimmed">
                  {t('settings.deadlines.hint')}
                </Text>
              </>
            )}
            {dayFields.map((field) => (
              <NumberInput
                key={field}
                label={t(`settings.deadlines.${field}`)}
                min={1}
                max={28}
                clampBehavior="strict"
                allowDecimal={false}
                value={deadlines[field]}
                onChange={(value) =>
                  setDeadlines((prev) => ({ ...prev, [field]: Number(value) || prev[field] }))
                }
                disabled={isLoadingSettings}
                error={field === 'scoringClosesDay' ? deadlinesError : undefined}
              />
            ))}

            {hasQuarterly && (
              <Switch
                label={t('settings.deadlines.scoringClosesNextMonth')}
                description={t('settings.deadlines.scoringClosesNextMonthHint')}
                checked={deadlines.scoringClosesNextMonth}
                onChange={(e) => {
                  const isChecked = e.currentTarget.checked;
                  setDeadlines((prev) => ({ ...prev, scoringClosesNextMonth: isChecked }));
                }}
                disabled={isLoadingSettings}
              />
            )}

            <Group>
              <Button
                onClick={handleSave}
                loading={isSaving}
                color="teal"
                disabled={deadlinesError !== null}
              >
                {t('settings.organization.save')}
              </Button>
            </Group>
          </Stack>
        </Card>
        <ModuleAccessSection />
      </Stack>
    </PageContainer>
  );
};

export default OrganizationSettings;
